import React from "react";

import { makeDelete } from "@culturehq/client";
import { Icon, PlainButton } from "@culturehq/components";

import { useProfile } from "app/context";
import { ProfileLink } from "app/links";
import { canAdminSocialShare } from "app/permissions";
import { Trans } from "app/utils";

import SocialChannel from "components/common/SocialChannel";
import QuickOptions from "components/display/QuickOptions";
import EngageLinkedin from "components/linkedin/EngageLinkedin";
import StoryQuestionTitle from "components/stories/StoryQuestionTitle";
import FeedItemTitle from "components/feed/feed-item/preview/FeedItemTitle";

import ChqModalConfirmDelete from "components/common/ChqModalConfirmDelete";
import Singular from "./story-created/Singular";
import PinUserActivityToggle from "../PinUserActivityToggle";
import SocialShareOption from "./SocialShareOption";

const PostQuickOptions = ({
  userActivity, onUserActivityUpdate, onUserActivityRemove
}) => {
  const { profile } = useProfile();
  const { entity: approvedForSocial } = userActivity;

  const onSocialShareDeleted = async () => {
    await makeDelete(`/stories/approved/${approvedForSocial.id}`);
    onUserActivityRemove(userActivity.id);
  };

  return (
    <QuickOptions>
      <ul className="dropdown-menu">
        {profile.organizationAdmin && (
          <li>
            <PinUserActivityToggle
              userActivity={userActivity}
              onUserActivityUpdate={onUserActivityUpdate}
            />
          </li>
        )}
        {canAdminSocialShare(profile, approvedForSocial) && (
          <li>
            <ChqModalConfirmDelete
              onAccept={onSocialShareDeleted}
              trigger={onTrigger => (
                <PlainButton onClick={onTrigger} className="link action">
                  <Icon icon="trash-a" />
                  <Trans>Delete</Trans>
                </PlainButton>
              )}
            >
              <Trans>Are you sure you want to delete this content approved for sharing?</Trans>
            </ChqModalConfirmDelete>
          </li>
        )}
      </ul>
    </QuickOptions>
  );
};

const LinkedInCreatorInfo = ({ story }) => {
  const { creator, linkedinShares } = story;
  const linkedinShare = linkedinShares ? linkedinShares[0] : undefined;

  if (linkedinShare?.socialCompany) {
    return (
      <>
        <span className="Linkedin_Creator--Information_Creator">{linkedinShare.socialCompany.name}</span>
        <Trans>&apos;s story on social!</Trans>
      </>
    );
  }

  if (linkedinShare?.creator?.id === creator.id) {
    return (
      <Trans>its story on social!</Trans>
    );
  }

  return (
    <>
      <ProfileLink user={creator} />
      <Trans>&apos;s story on social!</Trans>
    </>
  );
};

const SocialPostPreviewBody = ({ children, linkedinShare, story }) => {
  if (linkedinShare) {
    return (
      <EngageLinkedin story={story} linkedinShare={linkedinShare}>
        {children}
      </EngageLinkedin>
    );
  }

  return <>{children}</>;
};

const SocialPostPreview = ({
  approvedForSocial, onUserActivityRemove, onUserActivityUpdate, story, userActivity
}) => {
  const { question, linkedinShares } = story;
  const linkedinShare = linkedinShares ? linkedinShares[0] : undefined;

  return (
    <div className="FeedItem__preview__photo_created Story FeedItem__preview__social">
      <FeedItemTitle
        item={userActivity}
        date={userActivity.bumpedAt}
        user={linkedinShare?.creator || story.creator}
      >
        <ProfileLink user={linkedinShare?.creator || story.creator} />
        {linkedinShare?.creator ? (
          <span>
            {" "}
            <Trans>shared</Trans>
            {" "}
            <LinkedInCreatorInfo story={story} />
          </span>
        ) : (
          <span>
            <Trans>&apos;s story has been approved to share on social!</Trans>
          </span>
        )}
        <PostQuickOptions
          userActivity={userActivity}
          onUserActivityUpdate={onUserActivityUpdate}
          onUserActivityRemove={onUserActivityRemove}
        />
      </FeedItemTitle>
      <SocialPostPreviewBody linkedinShare={linkedinShare} story={story}>
        <div className="FeedItem__preview__Social__Container">
          <StoryQuestionTitle storyQuestion={question} showLink />
          <Singular
            item={userActivity.entity}
            user={story.creator}
            question={question}
          />
          <div className="FeedItem__preview__Social__Footer">
            <span><Trans>Share on:</Trans></span>
            <div className="SocialTags">
              {approvedForSocial.approvedForSocialMessages
                .filter(
                  (value, index, self) => self.findIndex(
                    element => element.socialType === value.socialType
                  ) === index
                ).map(approvedMessage => (
                  <SocialShareOption
                    key={approvedMessage.id}
                    approvedForSocial={approvedForSocial}
                    approvedMessage={approvedMessage}
                    story={story}
                  >
                    <PlainButton className="SocialOption__Container">
                      <SocialChannel socialType={approvedMessage.socialType} />
                    </PlainButton>
                  </SocialShareOption>
                ))}
            </div>
          </div>
        </div>
      </SocialPostPreviewBody>
    </div>
  );
};

const ApprovedForSocial = ({ onUserActivityRemove, onUserActivityUpdate, userActivity }) => {
  const { entity: approvedForSocial } = userActivity;
  const { entity: story } = approvedForSocial;

  return (
    <SocialPostPreview
      approvedForSocial={approvedForSocial}
      onUserActivityRemove={onUserActivityRemove}
      onUserActivityUpdate={onUserActivityUpdate}
      story={story}
      userActivity={userActivity}
    />
  );
};

export default ApprovedForSocial;
